/**
 * OpenAuthorContactsPage
 *
 * Articles in the "Open Author Contacts" workflow: PR articles whose extraction
 * is in progress / extracted and not yet uploaded (the status filter is applied
 * by the shared context's fetchPage for this sidebar option).
 *
 * This page differs from the standard table pages in two ways:
 *   - it offers continents / countries / study-years filters (and allows those
 *     columns to render), and
 *   - the country / study-year filter option lists are derived from the FULL
 *     Open Author Contacts dataset (loaded once here), not just the current
 *     page, so the available options stay stable while paging.
 */

import { useEffect, useMemo, useState } from "react"
import PrLiteratureReviewShell from "@/components/pr-literature-review/PrLiteratureReviewShell"
import JournalArticlesTable, {
  buildCollectionFilterOptions,
} from "@/components/journal-articles/JournalArticlesTable"
import { usePrLiteratureReviewPage } from "@/components/pr-literature-review/usePrLiteratureReviewPage"
import { getColumnGroupsForViewMode } from "@/components/pr-literature-review/columns"
import { FilterOption } from "@/components/shared/TableFiltersSelector"
import { useAuth } from "@/hooks/useAuth"
import {
  JournalArticle,
  getAllOpenAuthorContactsArticles,
} from "@/services/journalArticlesService"

/** Browser tab title and page heading. */
const PAGE_TITLE: string = "Open Author Contacts"

/** Short description shown under the page heading. */
const PAGE_DESCRIPTION: string =
  "Browse journal articles where we are still waiting for author contact for PR extraction"

/** The fixed list of continents offered in the Open Author Contacts filter. */
const CONTINENT_OPTIONS: string[] = [
  "Africa",
  "Asia",
  "Europe",
  "North America",
  "Oceania",
  "South America",
]

/**
 * The Open Author Contacts page.
 *
 * @returns The rendered page element.
 */
export default function OpenAuthorContactsPage(): React.JSX.Element {
  const page = usePrLiteratureReviewPage({ sidebarOption: "open-author-contacts" })
  const { getAccessToken } = useAuth()

  // The complete Open Author Contacts dataset, loaded once (and refreshed when
  // the current page is refetched) to populate the country / study-year filter
  // option lists from the full set rather than the current page.
  const [allArticles, setAllArticles] = useState<JournalArticle[]>([])

  // `lastFetchTime` is included so the option lists refresh after the current
  // page is re-fetched (e.g. following an edit that changes the dataset).
  const lastFetchTime: number | null = page.lastFetchTime
  useEffect(() => {
    let cancelled: boolean = false

    async function fetchAll(): Promise<void> {
      try {
        const token: string = await getAccessToken()
        const articles: JournalArticle[] = await getAllOpenAuthorContactsArticles(token)
        if (!cancelled) {
          setAllArticles(articles)
        }
      } catch (err) {
        // The option lists are a convenience, not core data, so a failure here
        // is logged but not surfaced as a page-level error.
        console.error("Failed to fetch Open Author Contacts articles for filters:", err)
      }
    }

    void fetchAll()
    return () => {
      cancelled = true
    }
  }, [getAccessToken, lastFetchTime])

  // The filter options for this page: the standard collection filters plus the
  // continents / countries / study-years filters unique to Open Author Contacts.
  // Countries and study years are derived from the full dataset (not the current
  // page) so the available options stay stable as the user pages through results.
  // The location filters are inserted directly after the Collections filter
  // (index 1) to preserve the original ordering.
  const filterOptions: FilterOption[] = useMemo(() => {
    const base: FilterOption[] = buildCollectionFilterOptions(page.collections)

    const countryOptions: string[] = [
      ...new Set(allArticles.flatMap((article) => article.countries ?? [])),
    ].sort()
    const studyYearOptions: string[] = [
      ...new Set(allArticles.flatMap((article) => (article.studyYears ?? []).map(String))),
    ].sort()

    const locationFilters: FilterOption[] = [
      { key: "continents", label: "Continents", options: CONTINENT_OPTIONS },
      { key: "countries", label: "Countries", options: countryOptions },
      { key: "studyYears", label: "Study Years", options: studyYearOptions },
    ]

    return [base[0], base[1], ...locationFilters, ...base.slice(2)]
  }, [page.collections, allArticles])

  return (
    <PrLiteratureReviewShell
      title={PAGE_TITLE}
      description={PAGE_DESCRIPTION}
      showDetailsPanel
      searchValue={page.searchQuery}
      onSearchChange={page.setSearchQuery}
    >
      {/* Open Author Contacts allows the location columns, so pass the page's
          visible columns through without stripping. */}
      <JournalArticlesTable
        page={page}
        filterOptions={filterOptions}
        visibleColumns={page.visibleColumns}
        columnGroupsForViewMode={getColumnGroupsForViewMode}
      />
    </PrLiteratureReviewShell>
  )
}
